import { useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Spinner, ErrorBoundary, ToastProvider } from '@bbpos/ui';
import { AppRouter } from './router';
import { useAuthStore } from '@/stores/use-auth-store';
import { useShiftStore } from '@/stores/use-shift-store';
import { useCartStore } from '@/stores/use-cart-store';
import { useLocationStore } from '@/stores/use-location-store';
import { useTenantStore } from '@/stores/use-tenant-store';
import { initAutoSync } from '@/services/sync-queue.service';
import { ensurePersistentStorage } from '@/services/storage-persistence.service';
import { startPeriodicSync } from '@/services/catalog-sync.service';
import { initStoreWiring } from '@/stores/store-wiring';
import { useRealtimeNotifications } from '@/hooks/use-realtime-notifications';
import { useSyncConflictsRealtime } from '@/hooks/use-sync-conflicts-realtime';
import { useAppHealthStore } from '@/stores/use-app-health-store';
import { AppHealthBanner } from '@/components/common/AppHealthBanner';
import { AppUpdateBanner } from '@/components/common/AppUpdateBanner';
import { InstallPromptBanner } from '@/components/common/InstallPromptBanner';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ShiftHydrator() {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const activeAttendance = useShiftStore((s) => s.activeAttendance);
  const loadActiveShift = useShiftStore((s) => s.loadActiveShift);
  const currentLocationId = useLocationStore((s) => s.currentLocationId);

  useEffect(() => {
    if (!isAuthenticated || !user || !profile) return;
    if (activeAttendance) return; // Already loaded
    if (!currentLocationId) return; // Wait for LocationHydrator to validate a location

    loadActiveShift(user.sub, currentLocationId).catch((err) => {
      useAppHealthStore.getState().setShiftHydrationError(
        err instanceof Error ? err.message : 'Failed to load shift',
      );
    });
  }, [isAuthenticated, user, profile, activeAttendance, loadActiveShift, currentLocationId]);

  return null;
}

function LocationHydrator() {
  const validateAndHydrate = useLocationStore((s) => s.validateAndHydrate);
  const currentLocationId = useLocationStore((s) => s.currentLocationId);
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Validate & restore the scoped location key once profile is available
  useEffect(() => {
    try {
      if (!isAuthenticated || !user || !profile) return;
      const tenantId = profile.tenant?.id;
      if (!tenantId) return;
      validateAndHydrate(tenantId, user.sub, profile.locations ?? []);
    } catch (err) {
      useAppHealthStore.getState().setLocationHydrationError(
        err instanceof Error ? err.message : 'Failed to restore location',
      );
    }
  }, [isAuthenticated, user, profile, validateAndHydrate]);

  // Auto-set location for single-location tenants who don't use shifts
  useEffect(() => {
    try {
      if (!isAuthenticated || !user || !profile) return;
      if (currentLocationId) return;

      const tenantId = profile.tenant?.id;
      if (!tenantId) return;

      const locations = profile.locations?.filter((l) => l.is_active) ?? [];
      if (locations.length === 1) {
        useLocationStore.getState().setLocation(
          locations[0].id,
          locations[0].name,
          locations[0].location_code,
          tenantId,
          user.sub,
        );
      }
    } catch (err) {
      useAppHealthStore.getState().setLocationHydrationError(
        err instanceof Error ? err.message : 'Failed to restore location',
      );
    }
  }, [isAuthenticated, user, profile, currentLocationId]);

  return null;
}

function CartHydrator() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Wait for isHydrated (set after beginDeviceSession installs the IDB
  // encryption key) so decryptFromStorage can read encrypted items. Without
  // this guard, hydrateFromIndexedDB runs before the key is installed and
  // silently returns an empty cart — breaking crash-recovery restores.
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const hydrateCart = useCartStore((s) => s.hydrateFromIndexedDB);

  useEffect(() => {
    if (!isAuthenticated || !isHydrated) return;
    hydrateCart().catch((err) => {
      useAppHealthStore.getState().setCartHydrationError(
        err instanceof Error ? err.message : 'Failed to restore cart',
      );
    });
  }, [isAuthenticated, isHydrated, hydrateCart]);

  return null;
}

function SyncInitializer() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Start auto-sync for offline queue and periodic catalog pull
    const cleanupAutoSync = initAutoSync();
    const cleanupCatalogSync = startPeriodicSync();

    return () => {
      cleanupAutoSync();
      cleanupCatalogSync();
    };
  }, [isAuthenticated]);

  return null;
}

function RealtimeNotificationsHydrator() {
  // Both hooks share the same Socket.IO connection opened by
  // useRealtimeNotifications (see socket-client.ts). Mounting
  // useSyncConflictsRealtime here lets it attach a `sync:conflict` listener
  // to that same socket without duplicating connect/disconnect logic.
  useRealtimeNotifications();
  useSyncConflictsRealtime();
  return null;
}

// Backend GET /tenants/:id requires @Roles('OWNER') — level 50+
const OWNER_PLUS_ROLES = new Set(['OWNER', 'ADMIN', 'MODERATOR', 'SUPER_ADMIN']);

function TenantHydrator() {
  const profile = useAuthStore((s) => s.profile);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const tenant = useTenantStore((s) => s.tenant);
  const fetchTenant = useTenantStore((s) => s.fetchTenant);
  const setFromAuthTenant = useTenantStore((s) => s.setFromAuthTenant);

  useEffect(() => {
    if (!isAuthenticated || !profile) return;

    const authTenant = profile.tenant;
    if (!authTenant?.id || tenant) return;

    // Only OWNER+ roles can call GET /tenants/:id — CASHIER/MANAGER take the
    // receipt profile /auth/me already carries.
    if (!OWNER_PLUS_ROLES.has(profile.role)) {
      setFromAuthTenant(authTenant);
      return;
    }

    fetchTenant(authTenant.id);
  }, [isAuthenticated, profile, tenant, fetchTenant, setFromAuthTenant]);

  return null;
}

function HydratorErrorFallback() {
  return null; // Hydrator errors are non-fatal — app continues without that data
}

function AppContent() {
  const hydrate = useAuthStore((s) => s.hydrate);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const wiringRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // T17: keep the offline queue's IndexedDB from being evicted.
  useEffect(() => {
    void ensurePersistentStorage();
  }, []);

  // Initialize cross-store wiring once
  useEffect(() => {
    wiringRef.current = initStoreWiring();
    return () => {
      wiringRef.current?.();
    };
  }, []);

  if (!isHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="app-splash">
        <Spinner variant="page" size="md" label="Starting Orio360..." />
      </div>
    );
  }

  return (
    <>
      <ErrorBoundary fallback={<HydratorErrorFallback />}>
        <LocationHydrator />
      </ErrorBoundary>
      <ErrorBoundary fallback={<HydratorErrorFallback />}>
        <ShiftHydrator />
      </ErrorBoundary>
      <ErrorBoundary fallback={<HydratorErrorFallback />}>
        <CartHydrator />
      </ErrorBoundary>
      <ErrorBoundary fallback={<HydratorErrorFallback />}>
        <SyncInitializer />
      </ErrorBoundary>
      <ErrorBoundary fallback={<HydratorErrorFallback />}>
        <TenantHydrator />
      </ErrorBoundary>
      <ErrorBoundary fallback={<HydratorErrorFallback />}>
        <RealtimeNotificationsHydrator />
      </ErrorBoundary>
      <AppUpdateBanner />
      <InstallPromptBanner />
      <AppHealthBanner />
      <AppRouter />
    </>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
